import type { BootstrapView, WalletProviderKey } from './types';

/**
 * Phase 1 tüketici kuralları — PWA ve native (Super APP / markalı uygulama) AYNI kararı versin diye tek yerde.
 * Saf fonksiyonlar (platform/ağ bilgisi parametre), testleri consumer-core'da.
 */

/**
 * Phase 1 akışı (telefon + ad ile kayıt → Wallet → üye sayfası): işletmede eski e-posta girişi KAPALIYSA. Capability listesi
 * gelmemişse (eski API) eski akış.
 */
export function isPhase1(bootstrap: BootstrapView | null | undefined): boolean {
  const caps = bootstrap?.capabilities;
  return Array.isArray(caps) && !caps.includes('auth.consumerEmailOtp');
}

/** Modül açık mı. Liste gelmemişse (eski API) açık say — eski davranış korunur. */
export function hasCapability(bootstrap: BootstrapView | null | undefined, key: string): boolean {
  const caps = bootstrap?.capabilities;
  return !Array.isArray(caps) || caps.includes(key);
}

/** Cihaza uygun Wallet: iOS → Apple, Android → Google, web/masaüstü → ikisi. */
export function walletsFor(os: 'ios' | 'android' | 'web' | string): WalletProviderKey[] {
  if (os === 'ios') return ['APPLE'];
  if (os === 'android') return ['GOOGLE'];
  return ['APPLE', 'GOOGLE'];
}

/** Harcama PIN kuralı — sunucu (MemberPinService.pinProblem) ile BİREBİR; istemci erken uyarır, sunucu yine doğrular. */
export function pinProblem(pin: string): string | null {
  if (!/^\d{4,6}$/.test(pin)) return 'PIN 4–6 haneli rakam olmalı.';
  if (/^(\d)\1+$/.test(pin)) return 'Tüm rakamları aynı olan PIN kullanılamaz.';
  const d = pin.split('').map(Number);
  const asc = d.every((v, i) => i === 0 || v === (d[i - 1] + 1) % 10);
  const desc = d.every((v, i) => i === 0 || v === (d[i - 1] + 9) % 10);
  if (asc || desc) return 'Ardışık rakamlardan oluşan PIN kullanılamaz.';
  return null;
}

/** Harcama karekodu isteğinin sonucu → ekranın bir sonraki adımı. */
export type SpendStep =
  | { kind: 'setPin' }
  | { kind: 'enterPin'; remaining?: number }
  | { kind: 'locked' }
  | { kind: 'verifyPhone' }
  | { kind: 'otp' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'error'; message: string };

export function spendStepFor(code: string | undefined, message: string, details?: { remaining?: number }): SpendStep {
  switch (code) {
    case 'PIN_NOT_SET':
      return { kind: 'setPin' };
    case 'PIN_REQUIRED':
      return { kind: 'enterPin' };
    case 'PIN_INVALID':
      return { kind: 'enterPin', ...(details?.remaining !== undefined ? { remaining: details.remaining } : {}) };
    case 'PIN_LOCKED':
      return { kind: 'locked' };
    case 'PHONE_VERIFICATION_REQUIRED':
      return { kind: 'verifyPhone' };
    case 'OTP_REQUIRED':
      return { kind: 'otp' };
    case 'OTP_UNAVAILABLE':
      return { kind: 'unavailable', message };
    default:
      return { kind: 'error', message };
  }
}

export type ConsumerTab = 'home' | 'card' | 'spend' | 'order' | 'offers' | 'account';

/**
 * Uygulama sekmeleri: modül listesi + işletme özelliklerinden. Kart ve hesap her zaman; harcama puan/ödül modülü açıksa;
 * sipariş yalnız `commerce.ordering` + mobil sipariş özelliği; teklifler özelliği açıksa.
 */
export function consumerTabs(bootstrap: BootstrapView | null | undefined, features: { mobileOrder?: boolean; offers?: boolean } = {}): ConsumerTab[] {
  const tabs: ConsumerTab[] = ['home', 'card'];
  if (hasCapability(bootstrap, 'loyalty.points') || hasCapability(bootstrap, 'loyalty.rewards')) tabs.push('spend');
  if (hasCapability(bootstrap, 'commerce.ordering') && features.mobileOrder) tabs.push('order');
  if (features.offers) tabs.push('offers');
  tabs.push('account');
  return tabs;
}

export interface BrandPalette {
  primary: string;
  onPrimary: string;
  accent: string;
  onAccent: string;
}

const DEFAULT_PRIMARY = '#0B6E4F';
const DEFAULT_ACCENT = '#D4AF37';

function hex(v: string | null | undefined): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(v?.trim() ?? '');
  return m ? `#${m[1].toUpperCase()}` : null;
}

/** WCAG göreli parlaklık. */
export function luminance(color: string): number {
  const h = hex(color) ?? '#000000';
  const ch = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** İşletme markasından uygulama paleti; renk yoksa Mayday varsayılanı, yazı rengi okunaklılığa göre. */
export function brandPalette(brand: { primaryColor?: string | null; accentColor?: string | null } | null | undefined): BrandPalette {
  const primary = hex(brand?.primaryColor) ?? DEFAULT_PRIMARY;
  const accent = hex(brand?.accentColor) ?? DEFAULT_ACCENT;
  // WCAG kontrast oranı: beyaz mı siyah mı daha okunaklı.
  const on = (c: string) => {
    const l = luminance(c);
    return (l + 0.05) / 0.05 > 1.05 / (l + 0.05) ? '#111111' : '#FFFFFF';
  };
  return { primary, onPrimary: on(primary), accent, onAccent: on(accent) };
}

/** "1.2" < "1.10"; eksik parça 0. Sunucudaki compareVersions ile aynı. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number(n) || 0);
  const pb = b.split('.').map((n) => Number(n) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d) return d < 0 ? -1 : 1;
  }
  return 0;
}

export type AppGate = { kind: 'ok' } | { kind: 'update'; storeUrl: string | null } | { kind: 'unavailable'; reason: string | null };

/**
 * Native uygulama açılış kapısı (bootstrap.app): işletme bu uygulamada yoksa "web'den devam", sürüm en düşük sürümün
 * altındaysa "güncelle". Web istemcisi (app yok) ve eski API her zaman geçer.
 */
export function appGate(bootstrap: BootstrapView | null | undefined, currentVersion: string | null | undefined, os: 'ios' | 'android' | string): AppGate {
  const app = bootstrap?.app;
  if (!app) return { kind: 'ok' };
  if (app.minVersion && currentVersion && compareVersions(currentVersion, app.minVersion) < 0) {
    return { kind: 'update', storeUrl: os === 'ios' ? app.appStoreUrl : os === 'android' ? app.playUrl : (app.appStoreUrl ?? app.playUrl) };
  }
  if (!app.allowed) return { kind: 'unavailable', reason: app.reason };
  return { kind: 'ok' };
}

/** Uygulama bağlantısı yolu: afiş `https://<alan>/k/<işletme>/…` (uygulama yüklüyse açılır), web `/<işletme>/…`. */
export const APP_LINK_PREFIX = 'k';

export interface TenantLink {
  slug: string;
  /** İşletme sonrası yol ('' | 'claim' | 'member' …). */
  rest: string;
  /** Sorgu parametreleri (sube, c …). */
  params: Record<string, string>;
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

/**
 * Afiş / paylaşılan bağlantı / elle girilen metin → işletme. Kabul: tam URL (https://…/k/slug, https://…/slug), özel şema
 * (maydaypos://k/slug), yol (/k/slug/claim?c=…) ya da yalın kısa ad. İşletme kısa adı değilse null.
 */
export function parseTenantLink(input: string | null | undefined): TenantLink | null {
  const raw = (input ?? '').trim();
  if (!raw) return null;
  let path = raw;
  let query = '';
  const scheme = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]*)(.*)$/i.exec(raw);
  if (scheme) {
    // https://host/path — özel şemada (maydaypos://k/slug) "host" yolun ilk parçasıdır.
    path = /^https?:/i.test(raw) ? scheme[2] : `/${scheme[1]}${scheme[2]}`;
  }
  const hash = path.indexOf('#');
  if (hash >= 0) path = path.slice(0, hash);
  const q = path.indexOf('?');
  if (q >= 0) {
    query = path.slice(q + 1);
    path = path.slice(0, q);
  }
  const parts = path.split('/').filter(Boolean).map((p) => decodeURIComponent(p));
  if (parts[0] === APP_LINK_PREFIX) parts.shift();
  const slug = (parts.shift() ?? '').toLowerCase();
  if (!SLUG_RE.test(slug)) return null;
  const params: Record<string, string> = {};
  for (const pair of query.split('&').filter(Boolean)) {
    const [k, v = ''] = pair.split('=');
    try {
      params[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, ' '));
    } catch {
      /* bozuk kodlama → yok say */
    }
  }
  return { slug, rest: parts.join('/'), params };
}

/**
 * Native uygulamaya gelen sistem bağlantısı → uygulama rotası. Yalnız `/k/<işletme>/…` (evrensel bağlantı ya da
 * <şema>://k/<işletme>) çevrilir: `/link?tenant=…&rest=…&<sorgu>`; diğer yollar null (olduğu gibi kalır).
 */
export function appLinkRoute(path: string): string | null {
  const raw = path.trim();
  const m = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]*)(.*)$/i.exec(raw);
  const rel = m ? (/^https?:/i.test(raw) ? m[2] : `/${m[1]}${m[2]}`) : raw;
  const first = rel.replace(/^\/+/, '').split(/[/?#]/)[0];
  if (first !== APP_LINK_PREFIX) return null;
  const link = parseTenantLink(raw);
  if (!link) return null;
  const q = Object.entries({ ...link.params, tenant: link.slug, ...(link.rest ? { rest: link.rest } : {}) })
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `/link?${q}`;
}
